import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { update } from '#~/hot-keys/commands/index.js';
import { DefaultHotKeys } from '#~/hot-keys/index.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { shallow } from 'zustand/shallow';
import { ButtonBox } from '../layout/index.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../types.js';
import { useWiring } from '../wiring.js';

/** Settings `<Footer>` component. */
export const Footer = (props: _Footer.Props): ReactElement => {
  const { hotKeys, isDefault, isDirty } = useStore(
    (µ) => pick(µ[Panel.HotKeys], ['hotKeys', 'isDefault', 'isDirty']),
    shallow
  );

  const producer = useProducer();
  const wiring = useWiring();
  const { track } = useAnalytics();

  const handleCommit = useLatestFn(() => {
    update(hotKeys);

    // Update snapshot.
    producer((draft) => {
      draft[Panel.HotKeys].snapshot = structuredClone(hotKeys);
    });

    // Track the save hotkey changes event
    track(EVENT_NAMES.SAVE_HOTKEY_CHANGES, EVENT_CATEGORIES.CONTROLLERS, {
      metadata: {
        changedHotkeysCount: Object.keys(hotKeys).length
      }
    });

    wiring.handleClose();
  });

  const handleReset = useLatestFn((): void => {
    producer((draft) => {
      draft[Panel.HotKeys].hotKeys = structuredClone(DefaultHotKeys);
    });
  });

  return (
    <_Footer.Root {...props}>
      <_Footer.Button
        disabled={isDefault}
        icon='reset'
        label='Reset to Default'
        onClick={handleReset}
      />

      <ButtonBox>
        <ButtonBox.Cancel onClick={wiring.handleClose} />
        <ButtonBox.Save disabled={!isDirty} onClick={handleCommit} />
      </ButtonBox>
    </_Footer.Root>
  );
};
